import type { CourseDto, Paginated, SectionDto } from '@hadir/shared';
import { useQuery } from '@tanstack/react-query';
import { BookOpen } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { HealthBanner } from '@/components/HealthBanner';
import { PageHeader } from '@/components/PageHeader';
import { Badge } from '@/components/ui/badge';
import { Button, buttonVariants } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorState } from '@/components/ui/error-state';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { api } from '@/lib/api';
import { formatDate } from '@/lib/format';
import { keys } from '@/lib/query-keys';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth.store';
import { useUiStore } from '@/stores/ui.store';

/**
 * Home — `/` (05 §2.2). The TA's landing screen: the courses they own, each with
 * how many sections it has, and one click into any of them.
 *
 * Section counts are derived from ONE `GET /sections` call grouped by course
 * rather than one call per card: the list endpoint is already scoped to the
 * signed-in TA server-side (`CourseAccessGuard`), so a single page of sections
 * answers every card at once.
 *
 * M2 replaces the "open attendance" affordance here; M1 has no open/close
 * endpoint, so a card links to the course.
 */
export default function HomePage() {
  const { t } = useTranslation();
  const lang = useUiStore((state) => state.lang);
  const actor = useAuthStore((state) => state.actor);

  useDocumentTitle(t('home.title'));

  const courses = useQuery({
    queryKey: keys.courses,
    queryFn: () => api.get<Paginated<CourseDto>>('/courses', { query: { limit: 100 } }),
  });

  const sections = useQuery({
    queryKey: keys.sections({}),
    queryFn: () => api.get<Paginated<SectionDto>>('/sections', { query: { limit: 100 } }),
  });

  const sectionCountByCourse = new Map<string, number>();
  for (const section of sections.data?.items ?? []) {
    sectionCountByCourse.set(
      section.courseId,
      (sectionCountByCourse.get(section.courseId) ?? 0) + 1,
    );
  }

  const items = courses.data?.items ?? [];

  return (
    <div className="space-y-6" data-testid="home-page">
      <PageHeader
        title={actor ? t('home.greeting', { name: actor.name }) : t('home.title')}
        description={t('home.description')}
        actions={
          <span className="text-sm text-muted-foreground">{formatDate(Date.now(), lang)}</span>
        }
      />

      <section aria-labelledby="home-courses-heading" className="space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <h2 id="home-courses-heading" className="text-base font-semibold">
            {t('home.myCourses')}
          </h2>
          <Link
            to="/courses"
            className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'ms-auto')}
            data-testid="home-all-courses"
          >
            {t('home.allCourses')}
          </Link>
        </div>

        {courses.isError ? (
          <ErrorState
            error={courses.error}
            onRetry={() => {
              void courses.refetch();
            }}
            data-testid="home-courses-error"
          />
        ) : courses.isPending ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" aria-busy="true">
            {Array.from({ length: 3 }, (_, index) => (
              <Card key={index}>
                <CardHeader>
                  <Skeleton className="h-5 w-40" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-4 w-24" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            title={t('home.noCourses')}
            description={t('home.noCoursesDescription')}
            icon={<BookOpen className="size-5" aria-hidden="true" />}
            data-testid="home-courses-empty"
            action={
              <Link to="/courses" className={cn(buttonVariants({ variant: 'default' }))}>
                {t('home.allCourses')}
              </Link>
            }
          />
        ) : (
          <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="home-course-list">
            {items.map((course) => {
              const count = sectionCountByCourse.get(course.id);
              return (
                <li key={course.id}>
                  <Card className="h-full">
                    <CardHeader>
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="outline" dir="ltr" className="font-mono">
                          {course.code}
                        </Badge>
                        {course.cohort ? (
                          <span className="text-xs text-muted-foreground">
                            {course.cohort.name}
                          </span>
                        ) : null}
                      </div>
                      <CardTitle>{course.name}</CardTitle>
                    </CardHeader>
                    <CardContent className="flex flex-wrap items-center gap-3">
                      <span className="text-sm text-muted-foreground tabular">
                        {sections.isPending
                          ? t('common.loading')
                          : sections.isError
                            ? t('home.sectionCountUnavailable')
                            : t('home.sectionCount', { n: count ?? 0 })}
                      </span>
                      <Link
                        to={`/courses/${course.id}`}
                        className={cn(
                          buttonVariants({ variant: 'outline', size: 'sm' }),
                          'ms-auto',
                        )}
                        data-testid={`home-course-${course.code}`}
                      >
                        {t('home.openCourse')}
                      </Link>
                    </CardContent>
                  </Card>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section aria-labelledby="home-health-heading" className="space-y-3">
        <h2 id="home-health-heading" className="text-base font-semibold">
          {t('health.title')}
        </h2>
        <HealthBanner />
      </section>

      {sections.isError ? (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            void sections.refetch();
          }}
        >
          {t('home.retrySectionCounts')}
        </Button>
      ) : null}
    </div>
  );
}
